import * as React from 'react';
import QuestionCircleOutlined from '@ant-design/icons/QuestionCircleOutlined';
import { isReactRenderable } from '@rc-component/util';
import { clsx } from 'clsx';
import convertToTooltipProps from '../_util/convertToTooltipProps';
import { isFunction } from '../_util/is';
import Col from '../grid/col';
import { useLocale } from '../locale';
import defaultLocale from '../locale/en_US';
import Tooltip from '../tooltip';
import { FormContext } from './context';
const FormItemLabel = ({ prefixCls, label, htmlFor, labelCol, labelAlign, colon, required, requiredMark, tooltip, vertical, }) => {
    const [formLocale] = useLocale('Form');
    const { labelAlign: contextLabelAlign, labelCol: contextLabelCol, labelWrap, colon: contextColon, classNames: contextClassNames, styles: contextStyles, tooltip: contextTooltip, } = React.useContext(FormContext);
    if (!isReactRenderable(label)) {
        return null;
    }
    const mergedLabelCol = labelCol || contextLabelCol || {};
    const mergedLabelAlign = labelAlign || contextLabelAlign;
    const isStartAlign = mergedLabelAlign === 'left' || mergedLabelAlign === 'start';
    const labelClsBasic = `${prefixCls}-item-label`;
    const labelColClassName = clsx(labelClsBasic, mergedLabelCol.className, {
        // Remove the legacy `-left` class in v7.
        [`${labelClsBasic}-start ${labelClsBasic}-left`]: isStartAlign,
        [`${labelClsBasic}-wrap`]: labelWrap,
    });
    let labelChildren = label;
    // Keep label is original where there should have no colon
    const computedColon = colon === true || (contextColon !== false && colon !== false);
    const haveColon = computedColon && !vertical;
    // Remove duplicated user input colon
    if (haveColon && typeof label === 'string' && label.trim()) {
        labelChildren = label.replace(/[:：]\s*$/, '');
    }
    const tooltipProps = convertToTooltipProps(tooltip, contextTooltip);
    if (tooltipProps) {
        const tooltipNode = (React.createElement(Tooltip, { ...tooltipProps },
            React.createElement("span", { className: `${prefixCls}-item-tooltip`, onClick: (e) => {
                    e.preventDefault();
                }, tabIndex: -1 }, tooltipProps.icon || tooltipProps.children || React.createElement(QuestionCircleOutlined, null))));
        labelChildren = (React.createElement(React.Fragment, null,
            labelChildren,
            tooltipNode));
    }
    // Required Mark
    const isOptionalMark = requiredMark === 'optional';
    const isRenderMark = isFunction(requiredMark);
    const hideRequiredMark = requiredMark === false;
    if (isRenderMark) {
        labelChildren = requiredMark(labelChildren, { required: !!required });
    }
    else if (isOptionalMark && !required) {
        labelChildren = (React.createElement(React.Fragment, null,
            labelChildren,
            React.createElement("span", { className: `${prefixCls}-item-optional` }, formLocale?.optional || defaultLocale.Form?.optional)));
    }
    // https://github.com/ant-design/ant-design/pull/52950#discussion_r1980880316
    let markType;
    if (hideRequiredMark) {
        markType = 'hidden';
    }
    else if (isOptionalMark || isRenderMark) {
        markType = 'optional';
    }
    const labelClassName = clsx(contextClassNames?.label, {
        [`${prefixCls}-item-required`]: required,
        [`${prefixCls}-item-required-mark-${markType}`]: markType,
        [`${prefixCls}-item-no-colon`]: !computedColon,
    });
    return (React.createElement(Col, { ...mergedLabelCol, className: labelColClassName },
        React.createElement("label", { htmlFor: htmlFor, className: labelClassName, style: contextStyles?.label, title: typeof label === 'string' ? label : undefined }, labelChildren)));
};
export default FormItemLabel;
